<template>
    <li class="my-8 bg-white p-6 rounded-2xl shadow-md border-l-2">
        <div class="flex flex-col items-start gap-y-4">
            <div class="relative w-full flex flex-wrap items-center justify-between text-gray-400">
                <!-- Asset name and more details icon-->
                <div class="flex items-center gap-x-2 whitespace-nowrap">
                    <a :href="todo.asset && todo.asset.id ? `assets/${todo.asset.id}` : '#'" target="_blank"
                       :class="[assetClass,
                       'font-medium text-lg text-gray-800 hover:text-gray-700 transition duration-300 text-left flex cursor-pointer']">
                        {{ todo.asset ? todo.asset.name : "Unspecified Asset" }}
                    </a>
                    <!--More Details Icon-->
                    <button class="flex items-center gap-x-1 hover:text-indigo-600 cursor-pointer transition duration-300" @click="toggleMoreDetails()">
                        <icon-annotation class="w-5 h-5"></icon-annotation>
                        <span>{{ showMoreDetails ? "Collapse" : "Details" }}</span>
                    </button>
                </div>

                <!-- Buttons top right -->
                <div class="flex flex-wrap items-center gap-x-2 text-gray-400 mr-6">
                    <badge v-if="todo.archived_at" class="bg-yellow-100 text-yellow-600">
                        Archived
                    </badge>

                    <div v-if="todo.completed_by" class="flex items-center gap-x-4 font-normal text-green-600">
                        Completed by {{ todo.completed_by.name }} on {{ todo.completed_at | date }}
                    </div>
                    <button v-else-if="!todo.archived_at" @click="confirmComplete()" title="Complete To-do">
                        <icon-thumbs-up class="w-6 h-6 hover:text-indigo-600 cursor-pointer transition duration-300" title="Complete"></icon-thumbs-up>
                    </button>
                </div>

                <!-- Absolute options menu-->
                <simple-dropdown button-class="" title="More Options" class="absolute -top-.5 right-0">
                    <template slot="labelHeader">
                        <icon-vertical-dots class="cursor-pointer mt-1 h-5 w-5 hover:text-indigo-600 transition duration-300"></icon-vertical-dots>
                    </template>

                    <template slot="buttonOptions">
                        <simple-dropdown-option @click="toggleEditModal()" value="Edit" v-if="!todo.archived_at && !todo.completed_at">
                            Edit
                        </simple-dropdown-option>

                        <simple-dropdown-option @click="reopen()" value="Reopen" v-if="todo.completed_at">
                            Reopen
                        </simple-dropdown-option>

                        <simple-dropdown-option @click="toggleArchive(true)" value="Archive" v-if="!todo.archived_at">
                            Archive
                        </simple-dropdown-option>
                        <simple-dropdown-option @click="toggleArchive(false)" value="Unarchive" v-if="todo.archived_at">
                            Unarchive
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>
            </div>

            <!--Description in middle of card -->
            <div class="font-normal text-left text-gray-400 select-none">{{ todo.description }}</div>

            <!-- More details beneath description when expanded -->
            <transition name="slide">
                <template v-if="showMoreDetails">
                    <dl class="w-full grid grid-cols-1 sm:grid-cols-4 gap-x-4 gap-y-8">
                        <template v-if="todo.ticket && todo.type === 'review ticket'">
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Ticket Primary Landlord</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.ticket.primary_landlord_name ? todo.ticket.primary_landlord_name : "None assigned" }}</dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Ticket Primary Tenant</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.ticket.primary_tenant_name ? todo.ticket.primary_tenant_name : "None assigned" }}</dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Assigned Service Provider</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.ticket.service_provider && todo.ticket.service_provider.name ? todo.ticket.service_provider.name : "None assigned" }}
                                    {{
                                        todo.ticket.service_provider && todo.ticket.service_provider.primary_contact && todo.ticket.service_provider.primary_contact.full_name ? " &middot; " +
                                            todo.ticket.service_provider.primary_contact.full_name : ""
                                    }}
                                </dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Status &middot; Phase</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.ticket.status | titlecase }} &middot; {{ todo.ticket.phase }}</dd>
                            </div>
                            <div class="sm:col-span-4" v-if="todo.ticket.latest_user_activity">
                                <dt class="text-sm font-medium text-gray-500">Last Activity</dt>
                                <dd class="mt-1 text-sm text-gray-800 flex flex-col">
                                    <span>
                                        {{ todo.ticket.latest_user_activity.activity }}
                                    </span>
                                    <span>
                                        {{ todo.ticket.latest_user_activity.notes }}
                                    </span>
                                </dd>
                            </div>
                            <div class="sm:col-span-4" v-if="todo.ticket.description">
                                <dt class="text-sm font-medium text-gray-500">Description</dt>
                                <dd class="mt-1 text-sm text-gray-800 flex flex-col pr-4 md:pr-2 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100"
                                    style="max-height: 120px !important;">
                                    <span class="whitespace-pre-line"> {{ todo.ticket.description }} </span>
                                </dd>
                            </div>
                        </template>

                        <template v-else-if="todo.inspection && todo.type === 'schedule inspection'">
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Inspection Primary Landlord</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.inspection.primary_landlord ? todo.inspection.primary_landlord : "None assigned" }}
                                </dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Inspection Primary Tenant</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.inspection.primary_tenant ? todo.inspection.primary_tenant : "None assigned" }}</dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Status &middot; Inspector</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.inspection.status | titlecase }} &middot; {{ todo.inspection.inspector_name }}</dd>
                            </div>
                        </template>

                        <template v-else>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Asset Primary Landlord</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.asset.landlord && todo.asset.landlord.contact_name ? todo.asset.landlord.contact_name : "None assigned" }}
                                </dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">Asset Primary Tenant</dt>
                                <dd class="mt-1 text-sm text-gray-800">{{ todo.asset.tenant && todo.asset.tenant.contact_name ? todo.asset.tenant.contact_name : "None assigned" }}</dd>
                            </div>
                            <div class="sm:col-span-4">
                                <dt class="text-sm font-medium text-gray-500">Full Address</dt>
                                <dd class="mt-1 text-sm text-gray-800 flex flex-col">
                                    <span>
                                        {{ todo.asset.full_address }}
                                    </span>
                                </dd>
                            </div>
                        </template>
                    </dl>
                </template>
            </transition>

            <!-- Todos type and ticket click through -->
            <div class="flex flex-wrap w-full items-center justify-between gap-4">
                <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <button @click="handleTypeClick()">
                        <badge :class="[getTodoTypeClass(), ' whitespace-nowrap transition duration-300']" text-size="text-sm">
                            {{ todo.type | titlecase }}
                        </badge>
                    </button>

                    <a v-if="todo.type === 'review ticket' && todo.ticket" :href="ticketUrl" target="_blank" class="font-medium">
                        <span :class="ticketPriorityClass">
                            {{ todo.ticket.ticket_reference_number }}
                        </span>
                    </a>

                    <a v-else-if="todo.type === 'schedule inspection' && todo.inspection" class="hover:text-indigo-600 transition duration-300 font-medium" target="blank"
                       :href="`/inspections/${todo.inspection.id}`">
                        {{ todo.inspection.type }} &middot; {{ todo.inspection.date | localeUnawareDatetime }}
                    </a>

                    <div class="flex items-center text-gray-400 gap-x-2 font-medium text-sm"
                         v-if="todo.latest_reminder && todo.latest_reminder.reminder_date">
                        <icon-calendar class="w-5 h-5"></icon-calendar>

                        <div class="flex flex-col items-start">
                            <span title="Reminder Date">{{ todo.latest_reminder.reminder_date | date }}</span>
                            <span class="text-xs">{{ todo.latest_reminder.repeat_text }}</span>
                        </div>

                        <model-replication-tooltip v-if="todo.replication_details" :replication-details="todo.replication_details"></model-replication-tooltip>
                    </div>
                </div>

                <div class="flex items-center">
                    <div v-if="todo.users && todo.users.length > 0" class="flex items-center">
                        <template v-if="todo.users.length > 5">
                            <div class="text-gray-400 mr-4 order-1 md:order-0 ml-1">
                                and {{ todo.users.length - 5 }} more
                            </div>
                        </template>
                        <template>
                            <div v-for="(user, index) in todo.users" class="order-0 md:order-1">
                                <a v-if="index < 5"
                                   class="-ml-1.5 text-white w-10 h-10 rounded-full flex justify-center items-center border-2 border-white transition duration-300 font-bold text-sm"
                                   :class="getAvatarColor(user.id)"
                                   :href="`users/profile/${user.id}`" target="_blank"
                                   :title="user.name">
                                    {{ user.initials }}
                                </a>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <confirmation-dialog
            :show="showConfirmCompleteDialog"
            title="Complete To-do"
            :subtitle="'This to-do has a repeat option enabled. Another to-do will be created for ' + repeatOnDate"
            :show-icon="false"
            cancel-button-text="Don't Repeat"
            proceed-button-text="Repeat"
            proceed-button-colour="indigo-600"
            proceed-button-hover-colour="indigo-400"
            @cancelled="complete(false)"
            @proceed="complete()"
        ></confirmation-dialog>
    </li>
</template>

<script>
export default {
    name: "TodoListItem",

    props: {
        todo: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        }
    },

    data () {
        return {
            showMoreDetails: false,
            showConfirmCompleteDialog: false,
        };
    },

    computed: {
        hasDueReminder () {
            if (!this.todo.latest_reminder) {
                return null;
            }

            return this.todo.latest_reminder.is_due;
        },

        assetClass () {
            switch (this.hasDueReminder) {
                case true:
                    return "font-bold text-brand hover:text-red-600";
                case false:
                    return "hover:text-indigo-600";
                default: {
                    return "text-yellow-400 hover:text-yellow-600";
                }
            }
        },

        ticketPriorityClass () {
            if (this.todo.ticket) {
                switch (this.todo.ticket.priority) {
                    case "Low":
                        return "text-green-600";
                    case "Medium":
                        return "text-blue-600";
                    case "High":
                        return "text-red-600";
                }
            }
        },

        ticketUrl () {
            if (this.todo.ticket) {
                return `/tickets/${this.todo.ticket.id}`;
            }
            return "#";
        },

        reminderDate () {
            if (this.todo && this.todo.latest_reminder && this.todo.latest_reminder.reminder_date) {
                return this.$dayjs(this.todo.latest_reminder.reminder_date).format("D MMM YYYY");
            }

            return null;
        },

        repeatOnDate () {
            if (!this.todo.latest_reminder || (this.todo.latest_reminder && this.todo.latest_reminder.repeat === "never")) {
                return null;
            }
            let reminder = this.todo.latest_reminder;
            if (reminder.repeat === "periodically") {
                let intervalType = reminder.interval_type;
                if (intervalType.endsWith("s")) {
                    intervalType = intervalType.substr(0, intervalType.length - 1);
                }
                return this.$dayjs(reminder.reminder_date).add(reminder.interval, intervalType).format("D MMM YYYY");
            } else if (reminder.repeat === "once") {
                return this.$dayjs(reminder.repeat_once_on).format("D MMM YYYY");
            }
        },
    },

    methods: {
        toggleMoreDetails () {
            this.showMoreDetails = !this.showMoreDetails;
        },

        getAvatarColor (userId) {
            if (userId === this.state.user.id) {
                return "bg-indigo-300 text-white hover:bg-white hover:text-indigo-600 hover:border-indigo-600";
            }
            return "bg-gray-100 text-gray-400 hover:bg-white hover:text-indigo-600 hover:border-indigo-600";
        },

        getTodoTypeClass () {
            switch (this.todo.type) {
                case "review ticket" :
                    return "bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:text-indigo-800 cursor-pointer";
                case "schedule inspection" :
                    return "bg-green-100 text-green-600" + (!this.todo.inspection ?
                        " hover:text-green-800 hover:bg-green-200 cursor-pointer" : " hover:text-gray-800 hover:bg-gray-200 cursor-not-allowed");
                default:
                    return "bg-gray-100 text-gray-400";
            }
        },

        handleTypeClick () {
            switch (this.todo.type) {
                case "review ticket" :
                    window.open(this.ticketUrl);
                    break;
                case "schedule inspection":
                    if (this.userCan("manage-inspections")) {
                        if (!this.todo.inspection) {
                            this.$emit("show-inspection-create-form");
                        } else {
                            toastr.warning("There is already a scheduled inspection");
                        }
                    } else {
                        toastr.error("You do not have permission to manage inspections");
                    }
                    break;
                default:
                    return null;
            }
        },

        confirmComplete () {
            if (!this.todo.processed_at && this.todo.latest_reminder && this.todo.latest_reminder.repeat !== "never") {
                this.showConfirmCompleteDialog = true;
                return;
            }
            this.complete();
        },

        complete (shouldRepeat = true) {
            this.showConfirmCompleteDialog = false;
            App.patch(`/todos/${this.todo.id}/complete`, new AppForm({
                should_repeat: shouldRepeat
            }))
            .then(response => {
                this.$emit("todo-changed");
                toastr.success("Successfully Completed To-do");
            })
            .catch(error => {
                toastr.error("Could not complete to-do. Error: " + error.message);
            });
        },

        reopen () {
            App.patch(`/todos/${this.todo.id}/reopen`, new AppForm({}))
            .then(response => {
                this.$emit("todo-changed");
                toastr.success("Successfully Reopened To-do");
            })
            .catch(error => {
                toastr.error("Could not reopen to-do. Error: " + error.message);
            });
        },

        toggleArchive (archived) {
            App.patch(`/todos/${this.todo.id}/toggle-archived`, new AppForm({}))
            .then(response => {
                this.$emit("todo-changed");
                toastr.success("Successfully  " + (archived ? "Archived" : "Unarchived") + " To-do");
            })
            .catch(error => {
                toastr.error("Could not " + (archived ? "archive" : "unarchive") + " to-do. Error: " + error.message);
            });
        },

        toggleEditModal () {
            this.$emit("toggle-create-edit-modal");
        },
    },
};
</script>
